import * as THREE from 'three/webgpu';
import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import { isFeatureFace, isFeatureLine, isFeaturePoint } from '../../mesh/feature';
import { isDatumAxis } from '../../reference/DatumAxis';
import { isDatumPlaneObject } from '../../reference/DatumPlane';
import { isDatumPoint } from '../../reference/DatumPoint';
import type { EntitySelectionManager } from '../EntitySelectionManager';
import { DefaultEntitySelectionHandler } from './DefaultEntitySelectionHandler';

/**
 * 基准轴模式实体选择处理器。
 *
 * 职责：
 * 1. 仅在基准轴命令期间处理拾取与移入高亮；
 * 2. 点击命中的特征点/线/面或正式基准点/轴/面后直接交由基准轴控制器消费；
 * 3. 禁止默认模式下的拖拽、框选与普通语义选择流程。
 */
export class ReferenceAxisEntitySelectionHandler extends DefaultEntitySelectionHandler {
    /**
     * @param entitySelectionManager 实体选择管理器。
     */
    public constructor(entitySelectionManager: EntitySelectionManager) {
        super(entitySelectionManager);
    }

    /**
     * 处理鼠标按下。
     * @param payload 原始事件。
     */
    public override onMouseDown(payload: RawPointerEventPayload): void {
        void payload;
    }

    /**
     * 处理鼠标移动。
     * @param payload 原始事件。
     */
    public override onMouseMove(payload: RawPointerEventPayload): void {
        if (payload.buttons !== 0) {
            this.clearHoverState();
            return;
        }
        this.queueHoverInput(payload.clientX, payload.clientY);
    }

    /** @inheritdoc 使用当前模式的候选规则消费最新悬停坐标。 */
    public override consumeHoverInput(clientX: number, clientY: number): void {
        const hoveredPick = this.pickEntityAt(
            clientX,
            clientY,
            this.collectReferenceAxisSelectionTargets(),
        );
        this.updateHoverState(this.resolveHoverPickWithSelectionExclusion(hoveredPick));
    }

    /**
     * 处理鼠标抬起。
     * @param payload 原始事件。
     */
    public override onMouseUp(payload: RawPointerEventPayload): void {
        void payload;
    }

    /**
     * 处理鼠标点击。
     * @param payload 原始事件。
     */
    public override onClick(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }
        if (this.entitySelectionManager.getSuppressNextClick()) {
            this.entitySelectionManager.setSuppressNextClick(false);
            return;
        }

        const rawPicked = this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            this.collectReferenceAxisSelectionTargets(),
        );
        const picked = this.resolveClickSelectionPick(rawPicked);
        if (!picked) {
            return;
        }

        if (
            !isFeatureFace(picked.object)
            && !isFeatureLine(picked.object)
            && !isFeaturePoint(picked.object)
            && !isDatumAxis(picked.object)
            && !isDatumPoint(picked.object)
            && !isDatumPlaneObject(picked.object)
        ) {
            return;
        }

        const viewer = this.entitySelectionManager.renderer;
        const accepted = viewer.referenceAxisController.handleSelectionObject(picked.object);
        if (!accepted) {
            viewer.drawingNoticeCallback?.('当前实体不支持作为基准轴参考', 'warning');
        }
    }

    /**
     * 收集框选候选对象。
     * 说明：
     * 1. 基准轴命令不支持框选流程；
     * 2. 返回空数组以禁用框选拾取路径，确保行为与命令约束一致。
     *
     * @returns 空候选列表。
     */
    protected override collectBoxSelectionTargets(): THREE.Object3D[] {
        return [];
    }

    /**
     * 收集测量叠加层点击候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureClickSelectionTargets(): THREE.Object3D[] {
        return this.collectReferenceAxisSelectionTargets();
    }

    /**
     * 收集测量叠加层移入候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureHoverSelectionTargets(): THREE.Object3D[] {
        return this.collectReferenceAxisSelectionTargets();
    }

    /**
     * 收集基准轴模式的可拾取对象。
     *
     * 设计说明：
     * 1. 基准轴命令链路当前接收特征点/线/面与正式基准点/轴/面；
     * 2. 仍然禁止把结果体外壳等泛化对象混入候选集；
     * 3. 这样可以保证控制器输入源单一，避免运行时再做多路对象分流。
     *
     * @returns 可参与拾取的对象列表。
     */
    private collectReferenceAxisSelectionTargets(): THREE.Object3D[] {
        const featureAndDatumTargets = this.collectDatumReferenceInteractionTargets(this.collectClickSelectionTargets()).filter((candidate) => (
            isFeatureFace(candidate)
            || isFeatureLine(candidate)
            || isFeaturePoint(candidate)
            || isDatumAxis(candidate)
            || isDatumPoint(candidate)
            || isDatumPlaneObject(candidate)
        ));
        return this.mergeImportedTopologySelectionTargets(featureAndDatumTargets);
    }
}
